// Memberships API layer.
//
// READs wrapped in `withFallback` so missing backend endpoints (404/501)
// resolve to typed empty payloads. Mutations (create/update/activate/
// deactivate/delete/enroll/cancel) are NOT wrapped — failures must reach
// the UI as toast errors.
//
// Backend reference: services/api/src/memberships/memberships.controller.ts
// and the enrollment endpoints on services/api/src/customers/customers.controller.ts.

import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
import type {
  CustomerMembership,
  EnrollCustomerInput,
  EnrolledMemberList,
  MembershipAggregateStats,
  MembershipPlan,
  MembershipPlanCreateInput,
  MembershipPlanListParams,
  MembershipPlanListResult,
  MembershipPlanStats,
  MembershipPlanUpdateInput,
} from './types';

/**
 * Backend endpoints referenced by the Memberships feature that do not yet
 * exist — or exist only partially — in services/api. The UI treats a 404/501
 * from any of these as an empty result (via withFallback) so the page
 * renders cleanly rather than throwing a red banner.
 *
 * Keep in sync with docs/gap-analysis-impl-vs-sdlc.md.
 *
 * Implemented in backend today (verified 2026-04-05):
 *   GET    /api/v1/memberships
 *   GET    /api/v1/memberships/:id
 *   POST   /api/v1/memberships
 *   PATCH  /api/v1/memberships/:id
 *   DELETE /api/v1/memberships/:id
 *   POST   /api/v1/customers/:id/membership          (enrollment)
 *   DELETE /api/v1/customers/:id/membership          (cancellation)
 *   GET    /api/v1/customers/:id/active-membership   (current active enrollment, null if none)
 */
export const BACKEND_GAPS = [
  'GET /api/v1/memberships/:id/stats',
  'GET /api/v1/memberships/:id/members',
] as const;

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&')}`;
}

// ─── Empty fallbacks ─────────────────────────────────────────────────────────

const emptyList: MembershipPlanListResult = {
  data: [],
  meta: { page: 1, limit: 20, total: 0, totalPages: 0 },
};

const emptyStats: MembershipPlanStats = {
  active_members: 0,
  total_enrollments: 0,
  lifetime_revenue: 0,
  cancelled_30d: 0,
  churn_rate_pct: null,
  avg_member_lifetime_days: null,
};

const emptyAggregateStats: MembershipAggregateStats = {
  total_members: 0,
  active_members: 0,
  total_revenue: 0,
  renewal_rate_pct: null,
};

const emptyMembers: EnrolledMemberList = {
  data: [],
  meta: { page: 1, limit: 20, total: 0, totalPages: 0 },
};

// ─── API ─────────────────────────────────────────────────────────────────────

export const membershipsApi = {
  async list(params: MembershipPlanListParams): Promise<MembershipPlanListResult> {
    // Backend accepts `search`, `is_active` ('true'|'false') and `tier_level`.
    // All filters are applied server-side so `meta.total` matches the rows.
    const isActive =
      params.status === 'ACTIVE' ? 'true' : params.status === 'INACTIVE' ? 'false' : undefined;
    return withFallback(
      async () => {
        const res = await apiClientPaginated<MembershipPlan[]>(
          `/memberships${buildQuery({
            page: params.page ?? 1,
            limit: params.limit ?? 20,
            search: params.search,
            is_active: isActive,
            tier_level: params.tier,
          })}`,
        );
        return { data: res.data, meta: res.meta };
      },
      emptyList,
    );
  },

  async get(id: string): Promise<MembershipPlan | null> {
    return withFallback(() => apiClient<MembershipPlan>(`/memberships/${id}`), null);
  },

  /**
   * Create a plan. Surfaces the backend uniqueness error (same tenant + name)
   * as a real error so the caller can map it onto the name field inline.
   */
  async create(input: MembershipPlanCreateInput): Promise<MembershipPlan> {
    return apiClient<MembershipPlan>('/memberships', { method: 'POST', body: input });
  },

  async update(id: string, input: MembershipPlanUpdateInput): Promise<MembershipPlan> {
    return apiClient<MembershipPlan>(`/memberships/${id}`, { method: 'PATCH', body: input });
  },

  /**
   * Activate / deactivate is a thin wrapper over PATCH {is_active}. The
   * backend doesn't have a dedicated toggle route — this keeps the call
   * site explicit and mirrors the voucher deactivate ergonomics.
   */
  async setActive(id: string, isActive: boolean): Promise<MembershipPlan> {
    return apiClient<MembershipPlan>(`/memberships/${id}`, {
      method: 'PATCH',
      body: { is_active: isActive },
    });
  },

  async remove(id: string): Promise<void> {
    await apiClient<void>(`/memberships/${id}`, { method: 'DELETE' });
  },

  async aggregateStats(): Promise<MembershipAggregateStats> {
    return withFallback(
      () => apiClient<MembershipAggregateStats>('/memberships/stats'),
      emptyAggregateStats,
    );
  },

  async stats(id: string): Promise<MembershipPlanStats> {
    return withFallback(() => apiClient<MembershipPlanStats>(`/memberships/${id}/stats`), emptyStats);
  },

  async enrolledMembers(id: string, page = 1, limit = 20): Promise<EnrolledMemberList> {
    return withFallback(
      async () => {
        const res = await apiClientPaginated<EnrolledMemberList['data']>(
          `/memberships/${id}/members${buildQuery({ page, limit })}`,
        );
        return { data: res.data, meta: res.meta };
      },
      emptyMembers,
    );
  },

  // ─── Customer-side enrollment (used by Customer 360 Memberships tab) ───

  async enrollCustomer(customerId: string, input: EnrollCustomerInput): Promise<CustomerMembership> {
    return apiClient<CustomerMembership>(`/customers/${customerId}/membership`, {
      method: 'POST',
      body: input,
    });
  },

  /**
   * The customer's currently active enrollment (or null if none). Wrapped in
   * withFallback so a tenant whose backend predates this route still renders.
   */
  async getActiveMembership(customerId: string): Promise<CustomerMembership | null> {
    return withFallback(
      () => apiClient<CustomerMembership | null>(`/customers/${customerId}/active-membership`),
      null,
    );
  },

  async cancelEnrollment(customerId: string, reason?: string): Promise<void> {
    await apiClient<void>(`/customers/${customerId}/membership`, {
      method: 'DELETE',
      body: reason ? { reason } : undefined,
    });
  },
};
